import { Avatar, Card, H1, H2, H3, H4, Icon, T, v, Image, Spinner, SpinnerCircle, Divider, H5, Button, ButtonIcon, styled, H6, th, Link, Switch, ListItem, ListIcon, RenderOnScreen } from '@veracity/vui';
import { Observer } from 'mobx-react-lite';
import React, { Fragment, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { identityClient } from '..';
import { Label } from '../components/Label';
import { LeftContainer } from '../components/LeftContainer';
import { Page } from '../components/Page';
import { UserItemRefAvatar } from '../components/UserItemRefAvatar';
import { CompanyRefDto } from '../models/auto-generated/CompanyRefDto';
import { ServiceItemRef } from '../models/ServiceItemRef';
import { UserItemRef } from '../models/UserItemRef';
import { UserOptInState } from '../models/UserOptInState';
import { OVERVIEW_ROUTE, ROUTES } from '../routes';
import { showAcceptedTermsDialogAsync } from '../showAcceptedTermsDialogAsync';
import { showNewTermsToAcceptDialogAsync } from '../showNewTermsToAcceptDialogAsync';
import { CancellationToken } from '../utils/CancellationToken';
import { toDateString } from '../utils/toDateString';
import { delayAsync } from '../utils/delayAsync';
import { OperationCancelledError } from '../utils/OperationCancelledError';
import { stringIsEmpty } from '../utils/stringIsEmpty';

// https://www.figma.com/file/3XVH46wO2NB1H23BCowTpl/Log--in-%2F-sign-up?node-id=6392%3A1367

export function TermsPage() {

	let [state, setState] = useState<{

		isLoading: boolean;
		allAvailableOptIns?: UserOptInState[];
		threw?: boolean;
		error?;

	}>({ isLoading: true });

	if (state.threw)
		throw state.error;

	useEffect(() => {

		let ct = new CancellationToken();

		(async () => {

			try {

				var allAvailableOptIns = (await identityClient.getAllAvailableOptInsAsync("!security")).filter(x => x.dto.Category === "terms");

				ct.throwIfCancellationRequested();
			}

			catch (e) {

				if (ct.isCancellationRequested)
					return;

				if (IS_DEVELOPMENT)
					console.debug(e);

				setState({
					isLoading: false,
					threw: true,
					error: e
				});

				return;
			}

			if (IS_DEVELOPMENT)
				console.debug("allAvailableOptIns", allAvailableOptIns);

			setState({
				isLoading: false,
				allAvailableOptIns
			});

		})();

		return ct.cancel.bind(ct);

	}, []);

	if (state.isLoading)
		return <Page key="LOADING_TERMS" center><Spinner /></Page>;

	return (
		<Page>
			<LeftContainer display="flex" flexDirection="column">
				<RenderOnScreen minWidth="lg">
					<H3>Terms</H3>
				</RenderOnScreen>
				<Observer>{() => {

					let platformOptInsList: UserOptInState[] = [];
					let serviceOptInsMap = new Map<string, { serviceName: string; serviceId: string; optIns: UserOptInState[]; }>();

					if (state.allAvailableOptIns) {
						for (let o of state.allAvailableOptIns) {
							if (shouldDisplayOptIn(o)) {
								if (stringIsEmpty(o.dto.ServiceId))
									platformOptInsList.push(o);
								else {
									let serviceOptInsEntry = serviceOptInsMap.get(o.dto.ServiceId);
									if (!serviceOptInsEntry)
										serviceOptInsMap.set(o.dto.ServiceId, serviceOptInsEntry = { serviceName: o.dto.ServiceName || o.service?.state.result?.dto.Name, serviceId: o.dto.ServiceId, optIns: [] });
									serviceOptInsEntry.optIns.push(o);
								}
							}
						}
					}

					return (
						<>
							{!!platformOptInsList.length && <v.div mt={40 / 8}>
								<T textTransform="uppercase" color="grey.80" fontWeight="demi" fontSize="sm" as="div">Platform</T>
								<OptInsCard title="Veracity" optIns={platformOptInsList} />
							</v.div>}
							{!!serviceOptInsMap.size && (
								<>
									<T textTransform="uppercase" color="grey.80" fontWeight="demi" fontSize="sm" mt={38 / 8} as="div">My services</T>
									{[...serviceOptInsMap].sort((a, b) => {
										let A = a[1].serviceName || a[0];
										let B = b[1].serviceName || b[0];

										return A < B ? -1 : B < A ? 1 : 0;
									}).map(x => x[1]).map(x => <OptInsCard key={x.serviceId} title={x.serviceName || x.serviceId} optIns={x.optIns} />)
									}
								</>
							)}
						</>
					);

				}}</Observer>
			</LeftContainer>
		</Page>
	);
}

export function shouldDisplayOptIn(optIn: UserOptInState) {
	let dto = optIn.dto;
	if (!stringIsEmpty(dto.Category) && dto.Category !== "terms" && dto.Mandatory)
		return false;
	return true;
}

function OptInsCard(props: { title: string; optIns: UserOptInState[]; }) {

	let termsOptIns: UserOptInState[] = [];
	let nonTermsOptIns: UserOptInState[] = [];

	for (let o of props.optIns) {
		if (o.dto.Category === "terms")
			termsOptIns.push(o);
		else nonTermsOptIns.push(o);
	}

	return (
		<Card w="100%" px={{ xs: 2, md: 4 }} py={{ xs: 2.5, md: 28 / 8 }} column shadow={0} border="1px solid grey.30" mt={2}>
			<H4>{props.title}</H4>
			<Divider my={2} />
			{termsOptIns.map(x => (
				<TermsContainer key={x.id}>
					<ListItem as="div" isInteractive iconLeft={<ListIcon mr={2} name="falFile" color="blue.80" />} iconRight={<ListIcon ml="auto" name="falChevronRight" color="blue.80" size="sm" />} border="1px solid grey.30" py={2} px={22 / 8} h={null} borderRadius="md" onClick={() => showAcceptedTermsDialogAsync(x)}>
						<div>
							<Observer>{() => <H6>{x.dto.Name}</H6>}</Observer>
							<Observer>{() => x.acceptedDate ? "Accepted on " + toDateString(x.acceptedDate) : null as any}</Observer>
						</div>
					</ListItem>
					<Observer>{() => x.dto.OptIn && !x.dto.OptIn.LatestVersion ? <v.div textAlign="right" mt={1}><Link isUnderlined as="span" role="button" tabIndex={0} userSelect="none" onClick={() => showNewTermsToAcceptDialogAsync(x).catch(e => { if (!(e instanceof OperationCancelledError)) console.error(e); })}>New version available</Link></v.div> : null as any}</Observer>
				</TermsContainer>
			))}
			{nonTermsOptIns.map(x => (
				<OptInContainer key={x.id} className="optIn">
					<v.div display="flex" alignItems="center">
						<v.div mr={2}>
							<H6><Observer>{() => x.dto.Name || null as any}</Observer></H6>
							<v.label><Observer>{() => x.dto.OptInText || null as any}</Observer></v.label>
						</v.div>
						<Observer>{() => <Switch size="lg" innerLabelOff="OFF" innerLabelOn="ON" ml="auto" checked={x.displayEnabledWithPendingTakenIntoAccount} disabled={x.pendingEnabled !== null} onChange={e => x.displayEnableDisableAsync(e.currentTarget.checked)} />}</Observer>
					</v.div>
				</OptInContainer>
			))}
		</Card>
	);
}

const TermsContainer = styled.div`

	&:not(:last-child) {
		margin-bottom: ${th.space(3)};
	}
`;

export const OptInContainer = styled.div`

	padding-bottom: ${th.space(2)};
	padding-top: ${th.space(2)};

	& + .optIn {
		border-top: 1px solid ${th.color("grey.30")};
	}

	&:first-child {
		padding-top: 0;
	}

	&:last-child {
		padding-bottom: 0;
	}
`;